import { action, observable } from 'mobx'
import { Brand, BrandDTO, Product } from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import { RootStore } from 'store/RootStore'
import { Api } from 'utilities'
import { SourceTypes } from './types'

export const BRAND_STORE = 'BRAND_STORE'

export class BrandStore {
  rootStore: RootStore;

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable brandsList: Brand[] = []
  @observable marketplaceBrandsList: Brand[] = []
  @observable brandToView: Brand = {} as Brand
  @observable statisticsData: any = null;

  @action.bound setMarketplaceBrandsList (list) {
    this.rootStore[GENERAL_STORE].setLoadingOff()
    this.marketplaceBrandsList = list
  }

  @action.bound setBrandsList (list: Brand[] = []) {
    this.brandsList = list
  }

  @action.bound setBrandToView (brand: Brand = {} as Brand) {
    this.brandToView = brand
  }

  @action.bound setStatisticsData = (data = null) => {
    this.statisticsData = data
  }

  @action.bound setBrandToViewProducts = (products: Product[]) => {
    this.brandToView.setProducts(products)
  }

  getBrands = (
    loadContents = false,
    loadProjects = false,
    source: SourceTypes = SourceTypes.STUDIO
  ) => {
    const { setLoadingOn, setLoadingOff, handleApiError } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    const fromSourceUrl = {
      [SourceTypes.STUDIO]: `/brand/all?loadContents=${loadContents}&loadProjects=${loadProjects}`,
      [SourceTypes.MARKETPLACE]: '/fromMarketplace/MARKETPLACE/brands',
      [SourceTypes.COCOS]: '/fromMarketplace/COCOS/brands',
      [SourceTypes.TECH]: '/fromMarketplace/TECH/brands'
    }[source]

    Api
      .get(fromSourceUrl)
      .then((brands: BrandDTO[]) => {
        setLoadingOff()
        if(source === SourceTypes.MARKETPLACE){
          brands.map(b => b.marketplaceType = 'MARKETPLACE')
        }
        this.setBrandsList(brands.map(b => new Brand(b)))
      })
      .catch(handleApiError)
  }

  // deprecated
  getMarketplaceBrands = (loadContents = false) => {
    const { setLoadingOn, handleApiError } = this.rootStore[GENERAL_STORE]
    setLoadingOn()
    Api
      .get(`/fromMarketplace/brand/all?loadContents=${loadContents}`)
      .then(this.setMarketplaceBrandsList)
      .catch(handleApiError)
  }

  getBrandById = async (
    id: string | number,
    loadContents = true,
    loadProjects = false,
    source: SourceTypes = SourceTypes.STUDIO) => {
    await this.getBrandByIdAndProject(id, loadContents, loadProjects, source, null)
  }

  getBrandByIdAndProject = async (
    id: string | number,
    loadContents = true,
    loadProjects = false,
    source: SourceTypes = SourceTypes.STUDIO,
    projectId?: number
  ) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    const url = {
      [SourceTypes.STUDIO]: `/brand/${id}/view?loadContents=${loadContents}&loadProjects=${loadProjects}` +
      (projectId ? `&projectId=${projectId}` : ''),
      [SourceTypes.MARKETPLACE]: `/fromMarketplace/MARKETPLACE/brands/${id}/products/?projectId=${document.URL.substring(document.URL.lastIndexOf('/') + 1)}`,
      [SourceTypes.COCOS]: `/fromMarketplace/COCOS/brands/${id}/products?projectId=${document.URL.substring(document.URL.lastIndexOf('/') + 1)}`,
      [SourceTypes.TECH]: `/fromMarketplace/TECH/brands/${id}/products?projectId=${document.URL.substring(document.URL.lastIndexOf('/') + 1)}`
    }[source]

    await Api
      .get(url)
      .then((res: BrandDTO) => {
        const brandToView = new Brand({
          [SourceTypes.STUDIO]: res,
          [SourceTypes.MARKETPLACE]: { ...this.brandsList.find(b => b.externalId === id), products: res },
          [SourceTypes.COCOS]: { ...this.brandsList.find(b => b.externalId === id), products: res },
          [SourceTypes.TECH]: { ...this.brandsList.find(b => b.externalId === id), products: res }
        }[source] as BrandDTO)
        this.setBrandToView(brandToView)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getBradProducts = (
    brandId: number | string,
    loadAgreements: boolean = false,
    loadProjects: boolean = false
  ) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    Api
      .get(
        `/brand/${brandId
        }/products?loadAgreements=${loadAgreements}&loadProjects=${loadProjects}`
      )
      .then((res: Product[]) => {
        this.setBrandToViewProducts(res)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  updateBrand = (brand: Brand) => {
    const cloneBrandsList = [...this.brandsList].filter(b => b.id !== brand.id)
    cloneBrandsList.push(brand)
    this.setBrandsList(cloneBrandsList)
  }

  createBrand = (brandDetails: Brand) => {
    const {
      setLoadingOn, showSuccess, push, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    Api
      .post('/brand/create', brandDetails)
      .then((res: any) => {
        if (brandDetails.relatedMedia && brandDetails.relatedMedia.length) {
          return Api.post('/brand/update', { id: res.id, ...brandDetails })
        }
        return new Promise((resolve) => resolve(res))
      })
      .then((res: Brand) => {
        this.updateBrand(res)
        showSuccess('Brand has been addeds.')
        push('/brands')
      })
      .catch(handleApiError)
  }

  editBrand = (brandDetails: BrandDTO) => {
    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError,
      // push,
      showSuccess
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    Api
      .post('/brand/update', brandDetails)
      .then((res: BrandDTO) => {
        this.setBrandToView(new Brand(res))
        this.setBrandsList()
        setLoadingOff()
        showSuccess('Details saved.')
        // push('/brands')
      })
      .catch(err => handleApiError(err))
  }

  generateStats = (body) => {
    const { setLoadingOn, setLoadingOff, handleApiError } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    Api
      .post('/stats/summaryReport', body, 'statsApi')
      .then(res => {
        this.setStatisticsData(res)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getBrandFromBrandListById = (brandId: number): Brand => {
    return this.brandsList.find(b => b.id === brandId)
  }

  clearStoreObservables = () => {
    this.setBrandsList()
    this.setBrandToView()
    this.setStatisticsData()
  }
}
